import React, { useEffect, useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { PageNames } from '../../../Constants/Common';
import {
    CosmeticRepairRequestType,
    CosmeticPageErrors,
    Panels
} from './CosmeticRepairRequestTypes';
import CosmeticRepairForm from './CosmeticRepairForm';
import './CosmeticRepairRequest.scss';
import CosmeticRequestsInfo from './CosmeticRequestsInfo';
import ErrorSummary from '../../common/ErrorSummary/ErrorSummary';
import FormInput from '../../common/FormInput';
import { GoogleAnalytics } from '../../../classes/GoogleAnalytics';
import ConfirmAlert from '../../common/confirmAlert/ConfirmAlert';
import ModalDialog from '../../common/ModalDialog';
import { t } from 'i18next';

export type CosmeticRepairRequestProps = {
    cosmeticRequests?: CosmeticRepairRequestType[];
    repairRequest?: CosmeticRepairRequestType;
    cosmeticFormError?: CosmeticPageErrors;
    showRepairRequestForm: boolean;
    showCancelAlert: boolean;
    cancelConfirmText: string;
    onChange: (key: string, value: string) => void;
    onConfirm: () => boolean;
    resetErrors: () => void;
    onChangeMileage: (event: React.FormEvent<HTMLInputElement>) => void;
    onChangeDamageType: (selectionName: string) => void;
    onChangeDamageDepth: (selectedValue: string) => void;
    showDamageDepthQuestion: () => boolean;
    onAddCosmeticRequest: () => void;
    onUploadImageChange: (
        imageType: string,
        uploadedPath: string,
        imageId: string,
        image: string,
        filename: string
    ) => void;
    onContinueToRepairAddress: () => void;
    onUpdateCosmeticRequest: (requestId: number) => void;
    onSelectedPanelChanged: (selected: boolean, panel: Panels) => void;
    onShowRepairRequestForm: (show: boolean) => void;
    onCancelClick: () => void;
    onCancelRepairRequest: () => void;
    onCancelUndo: () => void;
    onDeleteCosmeticRequest: (requestId: number) => void;
};

const CosmeticRepairRequestPage = ({
    cosmeticRequests,
    repairRequest,
    cosmeticFormError,
    showRepairRequestForm,
    showCancelAlert,
    cancelConfirmText,
    onChange,
    onConfirm,
    resetErrors,
    onChangeMileage,
    onChangeDamageType,
    onChangeDamageDepth,
    showDamageDepthQuestion,
    onUploadImageChange,
    onAddCosmeticRequest,
    onContinueToRepairAddress,
    onUpdateCosmeticRequest,
    onSelectedPanelChanged,
    onShowRepairRequestForm,
    onCancelClick,
    onCancelRepairRequest,
    onCancelUndo,
    onDeleteCosmeticRequest
}: CosmeticRepairRequestProps): JSX.Element => {
    const [showConfirmation, setShowConfirmation] = useState(false);
    useEffect(() => {
        GoogleAnalytics.setPageView(PageNames.CosmeticRepairRequests);
    }, []);
    const onShowConfirmation = (): void => {
        setShowConfirmation(true);
    };
    const onDeleteCosmeticRepairRequest = (): void => {
        setShowConfirmation(false);
        onDeleteCosmeticRequest(repairRequest.id);
        onClose();
    };
    const onClose = () => {
        resetErrors();
        onShowRepairRequestForm(false);
    };
    const onCloseConfirmation = (): void => {
        setShowConfirmation(false);
    };
    const errorlist = (): Array<string | undefined> => {
        const values = [cosmeticFormError?.cosmeticGeneral?.noCosmeticRequests];

        const errors = values.filter((error) => error !== undefined);

        return errors;
    };

    const repairRequestInfo =
        cosmeticRequests && cosmeticRequests.length > 0 ? (
            <CosmeticRequestsInfo
                cosmeticRequests={cosmeticRequests}
                onUpdateCosmeticRequest={onUpdateCosmeticRequest}
            />
        ) : (
            <h4 className='no-repair-requests-header'>
                {t('no_repair_requests_text')}
            </h4>
        );
    const forCancelButton = () => {
        const buttonText =
            cosmeticRequests.length > 1
                ? t('cancel_repair_requests')
                : t('cancel_repair_request');
        return (
            <button
                data-testid='danger-button'
                className='btn btn-danger progress-button'
                onClick={onCancelClick}>
                {buttonText}
            </button>
        );
    };
    const cancelButton = forCancelButton();
    return (
        <>
            <div className='card page-content'>
                <p className='p-3 m-0'>{t('cosmetic_repair_subheader')}</p>
                <div className='card-body page-content-body'>
                    <FormInput
                        id='repair-request-container-forminput'
                        label={t('repair_requests')}
                        errorText={
                            cosmeticFormError?.cosmeticGeneral
                                ?.noCosmeticRequests
                        }>
                        {repairRequestInfo}
                        <div className='add-repair-request-button-container'>
                            <hr />
                            <button
                                className='btn btn-primary'
                                onClick={() => onAddCosmeticRequest()}>
                                {t('add_repair_request')}
                            </button>
                        </div>
                    </FormInput>
                </div>

                {cosmeticFormError?.cosmeticGeneral?.noCosmeticRequests && (
                    <div className='card-footer page-content-footer'>
                        <ErrorSummary
                            title={t('cosmetic_errors')}
                            errors={errorlist()}
                            className='card-error'
                        />
                    </div>
                )}
            </div>

            <div className='bottom-navigation'>
                <button
                    className='btn btn-primary progress-button'
                    onClick={() => onContinueToRepairAddress()}>
                    {t('continue')}
                </button>
                {cancelButton}
            </div>
            <ConfirmAlert
                showAlert={showCancelAlert}
                onConfirm={onCancelRepairRequest}
                onCancel={onCancelUndo}
                alertText={cancelConfirmText}
            />
            <Modal
                isOpen={showRepairRequestForm}
                className='cosmetic-repair-request'>
                <ModalHeader toggle={onClose}>
                    {t('cosmetic_repair_request')}
                </ModalHeader>
                <ModalBody className='cosmetic-modal-form'>
                    {showRepairRequestForm && (
                        <CosmeticRepairForm
                            repairRequest={repairRequest}
                            formErrors={cosmeticFormError?.cosmeticGeneral}
                            onChange={onChange}
                            onChangeMileage={onChangeMileage}
                            onChangeDamageType={onChangeDamageType}
                            onChangeDamageDepth={onChangeDamageDepth}
                            errorSummaryTitle={t(
                                'repair_request_errors_heading'
                            )}
                            showDamageDepthQuestion={showDamageDepthQuestion}
                            onUploadImageChange={onUploadImageChange}
                            onSelectedPanelChanged={onSelectedPanelChanged}
                        />
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        className='btn btn-primary progress-button'
                        color='primary'
                        onClick={onConfirm}>
                        {t('confirm')}
                    </Button>
                    <Button
                        className='btn btn-danger progress-button'
                        onClick={onShowConfirmation}>
                        {t('delete')}
                    </Button>
                    <Button
                        className='btn btn-secondary progress-button'
                        onClick={onClose}>
                        {t('cancel')}
                    </Button>
                </ModalFooter>
            </Modal>
            <ModalDialog
                showDialog={showConfirmation}
                title={t('confirm_delete')}
                primaryButton={{
                    text: t('confirm'),
                    disabled: false,
                    onClick: onDeleteCosmeticRepairRequest
                }}
                secondaryButton={{
                    text: t('cancel'),
                    onClick: onCloseConfirmation
                }}
                onCloseClick={onCloseConfirmation}>
                {t('delete_cosmetic_repair_request')}
            </ModalDialog>
        </>
    );
};

export default CosmeticRepairRequestPage;
